import * as Cesium from 'cesium';
import { mapConfig } from '../config/mapConfig';

/**
 * Creates a terrain provider based on the specified provider ID
 * @param {string} providerId - The ID of the terrain provider to create
 * @returns {Promise<Cesium.TerrainProvider>} The created terrain provider
 */
export const createTerrainProvider = async (providerId) => {
  const providerConfig = mapConfig.terrain.providers.find(p => p.id === providerId);
  
  if (!providerConfig) {
    throw new Error(`Terrain provider configuration not found: ${providerId}`);
  }

  switch (providerConfig.type) {
    case 'cesium-ion':
      return await createCesiumIonTerrainProvider(providerConfig);
    
    case 'custom-url':
      return await createCustomUrlTerrainProvider(providerConfig);
    
    case 'ellipsoid':
      return new Cesium.EllipsoidTerrainProvider(providerConfig.options || {});
    
    default:
      throw new Error(`Unsupported terrain provider type: ${providerConfig.type}`);
  }
};

/**
 * Creates a Cesium Ion terrain provider
 */
const createCesiumIonTerrainProvider = async (config) => {
  if (config.requiresToken && !Cesium.Ion.defaultAccessToken) {
    throw new Error('Cesium Ion access token is required for this terrain provider');
  }

  try {
    console.log(`Creating Cesium Ion terrain provider with asset ID: ${config.assetId}`);
    const terrainProvider = await Cesium.CesiumTerrainProvider.fromIonAssetId(config.assetId, {
      ...config.options,
      ...mapConfig.terrain.performance
    });
    console.log('Cesium Ion terrain provider created successfully');
    return terrainProvider;
  } catch (error) {
    console.error('Failed to create Cesium Ion terrain provider:', error);
    
    // Check if it's an authentication issue
    if (error.message && error.message.includes('401')) {
      throw new Error('Cesium Ion authentication failed. Please check your access token.');
    }
    
    // Check if it's a network issue
    if (error.message && (error.message.includes('NetworkError') || error.message.includes('Failed to fetch'))) {
      throw new Error('Network error while loading Cesium Ion terrain. Please check your internet connection.');
    }
    
    // Re-throw with more context
    throw new Error(`Failed to load Cesium World Terrain: ${error.message}`);
  }
};

/**
 * Creates a custom URL terrain provider
 */
const createCustomUrlTerrainProvider = async (config) => {
  try {
    console.log(`Creating custom terrain provider from URL: ${config.url}`);
    const terrainProvider = await Cesium.CesiumTerrainProvider.fromUrl(config.url, {
      ...config.options,
      ...mapConfig.terrain.performance
    });
    console.log('Custom terrain provider created successfully');
    return terrainProvider;
  } catch (error) {
    console.error('Failed to create custom terrain provider:', error);
    
    // Check if it's a 404 or network issue
    if (error.message && (error.message.includes('404') || error.message.includes('Not Found'))) {
      throw new Error(`Terrain data not found at ${config.url}. This terrain source may not be available yet.`);
    }
    
    if (error.message && (error.message.includes('NetworkError') || error.message.includes('Failed to fetch'))) {
      throw new Error(`Network error while loading terrain from ${config.url}. Please check the URL and your internet connection.`);
    }
    
    // Re-throw with more context
    throw new Error(`Failed to load custom terrain from ${config.url}: ${error.message}`);
  }
};

/**
 * Validates if terrain data is available for a given region
 * @param {Cesium.TerrainProvider} terrainProvider 
 * @param {Cesium.Rectangle} region 
 * @returns {Promise<boolean>}
 */
export const validateTerrainAvailability = async (terrainProvider, region) => {
  try {
    if (!terrainProvider.availability) {
      return true; // Assume available if no availability data
    }
    
    // Check if the region intersects with available terrain data
    const availability = terrainProvider.availability;
    return availability.computeMaximumLevelAtPosition(
      Cesium.Rectangle.center(region)
    ) > 0;
  } catch (error) {
    console.warn('Failed to validate terrain availability:', error);
    return false;
  }
};

/**
 * Preloads terrain for a specific region to improve performance
 * @param {Cesium.Viewer} viewer 
 * @param {Cesium.Rectangle} region 
 */
export const preloadTerrainForRegion = async (viewer, region) => {
  try {
    const terrainProvider = viewer.terrainProvider;
    if (!terrainProvider || terrainProvider instanceof Cesium.EllipsoidTerrainProvider) {
      return; // No terrain to preload
    }

    // Preload terrain tiles for the region at multiple levels
    const promises = [];
    for (let level = 0; level <= 8; level++) {
      const tilingScheme = terrainProvider.tilingScheme;
      const startX = Math.max(0, Math.floor(tilingScheme.longitudeToTileX(region.west, level)));
      const endX = Math.min(
        tilingScheme.getNumberOfXTilesAtLevel(level) - 1,
        Math.floor(tilingScheme.longitudeToTileX(region.east, level))
      );
      const startY = Math.max(0, Math.floor(tilingScheme.latitudeToTileY(region.north, level)));
      const endY = Math.min(
        tilingScheme.getNumberOfYTilesAtLevel(level) - 1,
        Math.floor(tilingScheme.latitudeToTileY(region.south, level))
      );

      for (let x = startX; x <= endX; x++) {
        for (let y = startY; y <= endY; y++) {
          promises.push(
            terrainProvider.requestTileGeometry(x, y, level).catch(() => {
              // Ignore failed requests
            })
          );
        }
      }
    }

    await Promise.allSettled(promises);
    console.log(`Preloaded terrain for region:`, region);
  } catch (error) {
    console.warn('Failed to preload terrain:', error);
  }
};

/**
 * Optimizes terrain performance settings
 * @param {Cesium.Viewer} viewer 
 */
export const optimizeTerrainPerformance = (viewer) => {
  // Configure terrain for optimal performance
  if (viewer.terrainProvider && viewer.terrainProvider.maximumScreenSpaceError !== undefined) {
    viewer.terrainProvider.maximumScreenSpaceError = mapConfig.terrain.performance.maximumScreenSpaceError;
  }
  
  // Enable terrain caching
  viewer.scene.globe.tileCacheSize = mapConfig.terrain.performance.tileCacheSize;
  
  // Configure level-of-detail settings
  viewer.scene.globe.preloadAncestors = mapConfig.terrain.performance.preloadAncestors;
  viewer.scene.globe.preloadSiblings = mapConfig.terrain.performance.preloadSiblings;
  
  // Optimize for devices with WebGL2 support
  try {
    // Check for WebGL2 support more safely
    const canvas = document.createElement('canvas');
    const context = canvas.getContext('webgl2');
    const hasWebGL2 = !!context;
    
    if (hasWebGL2) {
      viewer.scene.globe.showWaterEffect = true;
      viewer.scene.globe.enableLighting = true;
      console.log('MapViewPage: WebGL2 detected, enabling advanced terrain effects');
    } else {
      console.log('MapViewPage: WebGL2 not available, using basic terrain rendering');
    }
  } catch (error) {
    console.warn('MapViewPage: Could not detect WebGL2 support:', error);
    // Continue without advanced effects
  }
};

/**
 * Cleans up terrain provider resources
 * @param {Cesium.TerrainProvider} oldProvider 
 */
export const cleanupTerrainProvider = (oldProvider) => {
  if (oldProvider && typeof oldProvider.destroy === 'function') {
    oldProvider.destroy();
  }
};

/**
 * Updates asset position to use terrain height
 * @param {Cesium.Viewer} viewer 
 * @param {Object} asset 
 * @returns {Promise<Cesium.Cartesian3>}
 */
export const placeAssetOnTerrain = async (viewer, asset) => {
  const position = Cesium.Cartesian3.fromDegrees(
    parseFloat(asset.center_lng),
    parseFloat(asset.center_lat)
  );
  
  // Sample terrain height at asset position
  const updatedPositions = await Cesium.sampleTerrainMostDetailed(
    viewer.terrainProvider,
    [Cesium.Cartographic.fromCartesian(position)]
  );
  
  if (updatedPositions.length > 0) {
    const terrainHeight = updatedPositions[0].height || 0;
    const assetHeight = parseFloat(asset.center_elevation || 0);
    
    return Cesium.Cartesian3.fromDegrees(
      parseFloat(asset.center_lng),
      parseFloat(asset.center_lat),
      terrainHeight + assetHeight
    );
  }
  
  return position;
};

/**
 * Positions 3D tileset on terrain
 * @param {Cesium.Viewer} viewer 
 * @param {Cesium.Cesium3DTileset} tileset 
 * @param {Object} asset 
 */
export const position3DTilesetOnTerrain = async (viewer, tileset, asset) => {
  if (!tileset.boundingSphere) return;
  
  const center = tileset.boundingSphere.center;
  const cartographic = Cesium.Cartographic.fromCartesian(center);
  
  // Sample terrain at the tileset location
  const samples = await Cesium.sampleTerrainMostDetailed(
    viewer.terrainProvider,
    [cartographic]
  );
  
  if (samples.length > 0) {
    const terrainHeight = samples[0].height || 0;
    
    // Apply height adjustment to tileset
    const heightOffset = terrainHeight - cartographic.height;
    if (Math.abs(heightOffset) > 1.0) { // Only adjust if significant difference
      const transform = Cesium.Matrix4.fromTranslation(
        Cesium.Cartesian3.fromDegrees(0, 0, heightOffset)
      );
      tileset.modelMatrix = Cesium.Matrix4.multiply(
        tileset.modelMatrix,
        transform,
        new Cesium.Matrix4()
      );
    }
  }
};

/**
 * Tests Cesium Ion connectivity and token validity
 * @returns {Promise<boolean>}
 */
export const testCesiumIonConnection = async () => {
  try {
    if (!Cesium.Ion.defaultAccessToken) {
      console.warn('No Cesium Ion access token found');
      return false;
    }
    
    console.log('Testing Cesium Ion connectivity...');
    
    // Try to create a simple terrain provider to test the token
    const testProvider = await Cesium.CesiumTerrainProvider.fromIonAssetId(1, {
      requestWaterMask: false,
      requestVertexNormals: false
    });
    
    console.log('Cesium Ion connection test successful');
    return true;
  } catch (error) {
    console.error('Cesium Ion connection test failed:', error);
    return false;
  }
}; 